/**
 * Events (brief sections 5 and 7.1): how an event is named and dated on
 * screen, and the shared notes of the home at a show, grouped by day.
 */

type EventLike = Readonly<Record<string, unknown>>;

const str = (v: unknown) => (typeof v === 'string' && v ? v : null);

/** "IFAT 2026": the series and its year, as the prototype names an event. */
export function eventName(e: EventLike): string {
  return [str(e.series), e.year ?? null].filter((v) => v !== null && v !== '').join(' ');
}

const day = (iso: string) => new Date(`${iso.slice(0, 10)}T12:00:00Z`);
const monthDay = (iso: string) =>
  day(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

/** "Oct 27 to Oct 30" (one date alone when the other is missing). */
export function eventDates(e: EventLike): string {
  const start = str(e.starts_on);
  const end = str(e.ends_on);
  if (start && end)
    return start.slice(0, 10) === end.slice(0, 10)
      ? monthDay(start)
      : `${monthDay(start)} to ${monthDay(end)}`;
  if (start) return `From ${monthDay(start)}`;
  if (end) return `Until ${monthDay(end)}`;
  return '';
}

/** "Munich, Germany, Oct 27 to Oct 30": the line under an event's name. */
export function eventPlace(e: EventLike): string {
  return [str(e.location), eventDates(e)].filter(Boolean).join(', ');
}

/** Active events first by start date, then completed ones, latest first. */
export function sortEvents<T extends EventLike>(events: readonly T[]): T[] {
  const start = (e: EventLike) => str(e.starts_on) ?? '9999';
  return [...events].sort((a, b) => {
    const ac = a.status === 'completed';
    const bc = b.status === 'completed';
    if (ac !== bc) return ac ? 1 : -1;
    return ac ? start(b).localeCompare(start(a)) : start(a).localeCompare(start(b));
  });
}

/** What is wrong with an event before it is saved (the server checks again). */
export function eventProblems(e: {
  series: string;
  year: string;
  starts_on: string;
  ends_on: string;
}): Partial<Record<'series' | 'year' | 'ends_on', string>> {
  const problems: Partial<Record<'series' | 'year' | 'ends_on', string>> = {};
  if (!e.series.trim()) problems.series = 'Event is required.';
  const year = Number(e.year);
  if (!Number.isInteger(year) || year < 2000 || year > 2100) problems.year = 'Year is required.';
  if (e.starts_on && e.ends_on && e.ends_on < e.starts_on) {
    problems.ends_on = 'The end date is before the start date.';
  }
  return problems;
}

export interface NoteGroup<T> {
  /** "Today, Thursday, Oct 29", "Tomorrow, Friday, Oct 30", "Any day". */
  title: string;
  today: boolean;
  notes: T[];
}

/**
 * The event notes by day (prototype: Notes for the event): dated days in
 * order, then the notes without a day; within a day, by time, then by
 * when they were written. `today` is the device's date, YYYY-MM-DD.
 */
export function noteGroups<T extends EventLike>(
  notes: readonly T[],
  today: string,
): NoteGroup<T>[] {
  const tomorrow = new Date(day(today).getTime() + 86_400_000).toISOString().slice(0, 10);
  const byDay = new Map<string, T[]>();
  for (const n of notes) {
    const key = str(n.day)?.slice(0, 10) ?? '';
    byDay.set(key, [...(byDay.get(key) ?? []), n]);
  }
  const order = (a: T, b: T) =>
    (str(a.time) ?? '99').localeCompare(str(b.time) ?? '99') ||
    String(a.created_at ?? '').localeCompare(String(b.created_at ?? ''));
  const label = (iso: string) =>
    day(iso).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  const groups: NoteGroup<T>[] = [...byDay.keys()]
    .filter((k) => k !== '')
    .sort()
    .map((k) => ({
      title:
        k === today ? `Today, ${label(k)}` : k === tomorrow ? `Tomorrow, ${label(k)}` : label(k),
      today: k === today,
      notes: byDay.get(k)!.sort(order),
    }));
  const anyDay = byDay.get('');
  if (anyDay) groups.push({ title: 'Any day', today: false, notes: anyDay.sort(order) });
  return groups;
}

/** "15:00" from the database's "15:00:00". */
export const noteTime = (time: unknown) => str(time)?.slice(0, 5) ?? null;
